import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  IActionMapping,
  ITreeOptions,
  ITreeState,
  KEYS,
  TREE_ACTIONS,
  TreeComponent,
  TreeModel,
  TreeNode,
} from '@circlon/angular-tree-component';
import { FilterContentItem } from '@portal/shared/ui/filter/src/lib/content/item';
import {
  BehaviorSubject,
  EMPTY,
  forkJoin,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
} from 'rxjs';
import {
  EntityStatus,
  EntityType as EntityTypeEnum,
  IEntity,
  IEntityAncestors,
  IEntityLightWeight,
  IOrganisation,
} from '@portal/entity-services/interfaces';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  switchMap,
  tap,
  filter,
} from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FilterEntityComponent } from '@portal/shared/ui/filter/src/lib/entity/entity.component';
import { AuthenticationService } from '@portal/shared/auth/authentication/src/lib/authentication.service';
import { OrganisationService } from '@portal/entity-services/organisations/src/lib/services/organisation.service';
import { FILTER_CONTENT_ITEM } from '@portal/shared/ui/filter/src/lib/content/token';
import { ISelectInput } from '@portal/shared/ui/filter/src/lib/box/interfaces/select-input.interface';
import { cloneDeep, forEach, uniqBy } from 'lodash';
import { LanguageHandle } from '@portal/shared/languages';
import { IFilterToApply } from '@portal/shared/ui/filter/src/lib/search/interfaces/filter-to-apply.interface';
import { ISearchCriteria } from '@portal/shared/ui/filter/src/lib/search/interfaces/search-criteria.interface';
import { SearchSelector } from '@portal/shared/ui/filter/src/lib/search/enums/search-selector.enum';
import { SearchOperator } from '@portal/shared/ui/filter/src/lib/search/enums/search-operator.enum';
import { FilterAutoCompleteInputComponent } from '@portal/shared/ui/filter/src/lib/autocomplete/search-autocomplete-input.component';
import { HttpError } from '@portal/shared/error-handler/src/lib/enums/HttpError';
import { FilterSubMenu } from '@portal/shared/ui/filter/src/lib/enums/filter-sub-menu.enum';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { SearchParameterList } from './search-parameters.list';
import { SearchParameters } from '@portal/shared/ui/filter/src/lib/enums/search-parameters.enum';
import { ErrorService } from '@portal/shared/ui/form';
import { NotifierService } from '@portal/shared/ui/notifier';
import get from 'lodash/get';
import { FiltersService } from '@portal/shared/ui/filter/src/lib/services/filters.service';
import { DOCUMENT, KeyValue } from '@angular/common';
import { AuthorizationService } from '@portal/shared/auth/authorization/src/lib/authorization.service';
import { IEntityHierarchy } from '@portal/entity-services/interfaces/src/lib/organisations/interfaces/entity-hierarchy.interface';
import { Directions } from '@portal/shared/core/src/lib/enums/direction.enum';
import { MobileViewService } from '../../../../../services/mobile-view.service';
import { Features } from '../../../../../../../../../environments/enums/features.enum';
import { FeatureToggle } from '@portal/shared/auth/feature-toggle';
import { CompareService } from '@portal/shared/helpers/src/lib/compare/compare.service';
import { ReadableEntityTypeService } from '@portal/entity-services/organisations/src/lib/services/readable-entity-type.service';

declare const $localize;
type SelectedNode = IEntity & { children: SelectedNode[] };

interface IAllPreSelectedEntityValues {
  id: string;
  text: string;
}

const SINGLE_ENTITY = 1;

@UntilDestroy()
@Component({
  selector: 'portal-transaction-hierarchy-filters',
  templateUrl: './hierarchy-filter.component.html',
  styleUrls: ['./hierarchy-filter.component.scss'],
  providers: [
    {
      provide: FILTER_CONTENT_ITEM,
      useExisting: TransactionHierarchyFilterComponent,
    },
  ],
  standalone: false,
})
export class TransactionHierarchyFilterComponent
  extends FilterContentItem
  implements OnInit, OnDestroy, OnChanges
{
  static initialDebounce = 1000;

  @Input() resetEntity$ = new Subject();
  @Input()
  set searchSelector(val) {
    this._searchSelector = val;
  }

  get searchSelector(): any {
    return this._searchSelector;
  }

  @Input() isSubMenuFilter: boolean;
  @Input() isMainDropdownFilter: boolean;
  @Input() displayFromFilter: boolean;
  @Input() showFilterSubMenu: boolean;
  @Input() selectedParentEntity: IEntity;
  @Input() selectedParentEntityChanged: boolean;

  @Input()
  set filterName(val) {
    this.entityText = val;
  }

  @Input() entityType = '';
  @Input() numberOfSelectedEntities?: number;
  @Input() isPreSelectedAncestorIds: boolean;
  @Input() entityRemoved$ = new Subject<ISelectInput>();
  @Input() selectedEntities: Map<string, ISearchCriteria>; // Pre-selected, from store
  @Input() selectLimit = 0;
  @Input() notFoundMessage = $localize`No result found`;
  @Input() organizationGroup: boolean;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('control') controlEntityUids: AbstractControl = new UntypedFormControl();
  @Input() selectedSubOrgToEdit: IOrganisation[];
  @Input() groupModeEdit: boolean;
  @Input() allowDeleted = false;
  @Input() editSchedulerHierarchyFilter: boolean;
  @Input() isScheduler: boolean;
  @Input() isDropdownHeight = false;
  @Input() isPreSelectedOrganizationNumber: number;
  @Input() disabledApply = false;
  @Output() listAllEntities: EventEmitter<ISelectInput[]> = new EventEmitter();
  @Output() organisationTextName: EventEmitter<any> = new EventEmitter();
  @Output() filterApplied: EventEmitter<IFilterToApply> = new EventEmitter();
  @Output() filterUpdated: EventEmitter<IFilterToApply> = new EventEmitter();
  @Output() staticFilterUpdated: EventEmitter<IFilterToApply> = new EventEmitter();
  @Output() selectedAncestorsFilterUpdated: EventEmitter<IFilterToApply> = new EventEmitter();
  @Output() isExceededSelectedAncestors: EventEmitter<boolean> = new EventEmitter();
  @Output() selectedAncestorsSchedulerFilterUpdated: EventEmitter<IFilterToApply> =
    new EventEmitter();
  @Output() selectedAncestorEntitiesNumber: EventEmitter<number> = new EventEmitter();
  @Output() allPreSelectedEntitiesNumber: EventEmitter<number> = new EventEmitter();
  @Output() isParentSelectedEmitter: EventEmitter<boolean> = new EventEmitter(false);
  @Output() filterAppliedSelectedEntities: EventEmitter<Map<string, ISelectInput>> =
    new EventEmitter();
  @Output() resetParentEntityChanged: EventEmitter<boolean> = new EventEmitter();
  @Output() openDropDown: EventEmitter<boolean> = new EventEmitter();
  @Output() buttonFocused: EventEmitter<boolean> = new EventEmitter();
  @Output() entityListGroupOrganisationUpdated: EventEmitter<string[]> = new EventEmitter();
  @ViewChild('tree') tree: TreeComponent;
  @ViewChild('applyButton') applyButton: ElementRef;
  @ViewChild('upArrow') upArrow: ElementRef;
  @ViewChild('downArrow') downArrow: ElementRef;
  @ViewChild('transactionSearchInput', { static: true })
  transactionSearchInput: FilterAutoCompleteInputComponent;
  @ViewChild(forwardRef(() => FilterAutoCompleteInputComponent))
  searchInput: FilterAutoCompleteInputComponent;

  set entityText(val) {
    this._entityText = val;
  }

  get entityText(): string {
    return this._entityText;
  }

  title = '';
  staticListUpdateSubject$ = new Subject<IFilterToApply>();
  itemToggled: EventEmitter<number> = new EventEmitter();
  isOpen = false;
  localPreSelectedEntities: Map<string, ISelectInput> = new Map();
  intermediatePreSelectedEntities: Map<string, ISelectInput> = new Map();
  selectedEntitiesStored = new Map();
  allPreSelectedEntities: Map<string, ISelectInput> = new Map();
  allPreSelectedEntityValues: IAllPreSelectedEntityValues[] = [];
  localPreSelectedAncestorEntities: Map<string, ISelectInput> = new Map();
  localPreSelectedEntitiesOnClick: Map<string, ISelectInput> = new Map();
  selectNode: { click: boolean; event: Record<string, any> };
  deselectNode: { click: boolean; event: Record<string, any> };
  entitiesAll: IEntity[] = [];
  entities: IEntity[] = [];
  allEntities: ISelectInput[];
  searchedText = '';
  isOrgExist = true;
  loading$: Observable<boolean>;
  showSelectedEntities: Map<string, ISelectInput> = new Map();
  searchTerm$ = new Subject<string>();
  search$: Observable<IOrganisation[]>;
  debounce = 1000;
  filterLabel = this.title;
  treeState: ITreeState;
  currentRootEntity = this.authService.entityUid;
  groupEntityRelationships: IEntityLightWeight[] = [];
  entityChildren: IEntityHierarchy[] = [];
  treeActionsMapping: IActionMapping = {
    keys: {
      [KEYS.SPACE]: TREE_ACTIONS.TOGGLE_SELECTED,
      [KEYS.ENTER]: () => {
        this.onFilterApplied();
      },
    },
  };

  rootParentNodeData: IOrganisation;
  localExpandedHierarchyList: Map<string, IOrganisation> = new Map();
  limitSelectedEntityElements = 20;
  ancestorEntitiesElements = 0;
  addButtonLabel: string;

  options: ITreeOptions = {
    getChildren: (node: TreeNode) => {
      this.setCurrentNodeExpanded(node.data);
      return this.isEntityTypeGroup && node?.data?.entityType === EntityTypeEnum.GROUP
        ? this.callGetGroupRelationships(node)
        : this.callGetHierarchyView(node);
    },
    useCheckbox: true,
    idField: 'entityUid',
    displayField: 'name',
    useVirtualScroll: false,
    nodeHeight: 22,
    actionMapping: this.treeActionsMapping,
  };
  loadingNewPortion$: Observable<boolean>;
  loadingMore$ = new BehaviorSubject(0);
  backToMenuSubject$ = new Subject<IFilterToApply>();
  backToMenuSub: Subscription;
  isActive = true;
  searchParameterList = this.searchParameters.list;
  searchParameterType = SearchParameters.Name;
  scrollPosition: number;
  entityIdListGroupOrganisation: string[] = [];
  entityListGroupOrganisation = [];
  flatternEntityListWithChildren = [];
  isDeviceMobile$: Observable<boolean>;
  isMobile = false;
  selectedEntityCount = 0;
  isFriendlyNameFeatureActive: boolean;
  selectedEntityList: Map<string, any> = new Map();
  intermediateselectedEntityList: Map<string, any> = new Map();
  isLastBatch = false;
  compareFn: (a: KeyValue<string, any>, b: KeyValue<string, any>) => number;

  get searchPlaceholder(): string {
    return $localize`Search by ${
      this.searchParameterList.find((input) => input.id === this.searchParameterType).text
    }`;
  }

  includeDeleted = this.allowDeleted && this.preselectedIncludeDeleted;

  get status(): EntityStatus[] {
    return this.includeDeleted
      ? [EntityStatus.Active, EntityStatus.Inactive, EntityStatus.Deleted]
      : [EntityStatus.Active, EntityStatus.Inactive];
  }

  get preselectedStatus(): EntityStatus[] {
    const values = this.selectedEntities?.get(SearchSelector.Status)?.values as Set<EntityStatus>;
    return values && [...values];
  }

  get preselectedIncludeDeleted(): boolean {
    return this.preselectedStatus ? this.preselectedStatus.includes(EntityStatus.Deleted) : true;
  }

  get isParentSelected(): boolean {
    if (this.organizationGroup && this.allPreSelectedEntityValues?.length === SINGLE_ENTITY) {
      return this.allPreSelectedEntityValues[0].id === this.selectedParentEntity?.entityUid;
    }
    return false;
  }

  get isUpArrowDisabled(): boolean {
    return this.entityFlex <= 0;
  }
  get isDownArrowDisabled(): boolean {
    return this.selectedEntityList.size === 0 || this.treeFlex <= 0;
  }

  readonly Directions = Directions;
  readonly WrapperHeight = 100; // Total height of the wrapper in percentage
  readonly arrowControlsHeight = 10; // Height for the arrow controls in percentage

  entityFlex = 0; // Initial height for the entity list
  treeFlex = this.WrapperHeight - this.entityFlex - this.arrowControlsHeight; // Initial height for the tree

  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _start: number;
  private stopLoading: boolean;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _searchSelector: any = SearchSelector.EntityId;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  private _entityText = $localize`Organisations`;
  private loadingStatus = false;
  private selectedPath: string[];
  private selectedNodes: SelectedNode[];
  private removeOne = false;
  private isEntityTypeGroup = false;

  constructor(
    public organisationService: OrganisationService,
    private authService: AuthenticationService,
    private languageHandle: LanguageHandle,
    private searchParameters: SearchParameterList,
    private notifierService: NotifierService,
    private filtersService: FiltersService,
    private authorizationService: AuthorizationService,
    private mobileViewService: MobileViewService,
    private readableEntityTypeService: ReadableEntityTypeService,
    private featureToggleService: FeatureToggle,
    private compareService: CompareService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    super();
    this.loading$ = organisationService.loading$;
    this.loading$.subscribe(() => {
      setTimeout(() => {
        const treeModel = this.tree?.treeModel as any;

        if (treeModel && !treeModel._patchedSetActiveNode) {
          const originalSetActiveNode = treeModel.setSelectedNode;

          treeModel.setSelectedNode = (node: any, value: any, multi?: boolean) => {
            this.saveScrollPosition();

            originalSetActiveNode.call(treeModel, node, value, multi);

            setTimeout(() => {
              this.restoreScrollPosition();
            }, 0);
          };

          treeModel._patchedSetActiveNode = true;
        }
      }, 0);
    });
    this.loadingNewPortion$ = organisationService.loadingNewPortion$.pipe(
      tap((value) => (this.loadingStatus = value)),
    );
    this.isDeviceMobile$ = this.mobileViewService.mobileDevice;
    this._start = 0;

    this.isEntityTypeGroup =
      this.authorizationService['user']?.entity?.entityType === EntityTypeEnum.GROUP;
    this.addButtonLabel = $localize`Add`;
    this.compareFn = this.compareService.compareFn('value.data.name');
  }

  setCurrentNodeExpanded(nodeData: IOrganisation): void {
    if (!this.localExpandedHierarchyList.get(nodeData?.entityUid)) {
      this.localExpandedHierarchyList.set(nodeData.entityUid, {
        count: nodeData.count,
        entityUid: nodeData.entityUid,
        name: nodeData.name,
        hasChildren: nodeData.hasChildren,
        parentEntityUid: null,
      });
    }
  }

  setLocalExpandedHierarchyList(hierarchyList: IOrganisation[]): void {
    hierarchyList.forEach((entity) => {
      if (!this.localExpandedHierarchyList.get(entity?.entityUid)) {
        this.localExpandedHierarchyList.set(entity.entityUid, {
          count: entity.count,
          entityUid: entity.entityUid,
          name: entity.name,
          hasChildren: entity.hasChildren,
          parentEntityUid: entity.parentEntityUid,
        });
      }
    });
  }

  getNodeByEntityUid(entityUid: string): IOrganisation {
    return this.localExpandedHierarchyList?.get(entityUid);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Adding a group organization group:
    // If the parent organization changed, the sub organizations will be updated according
    if (this.selectedParentEntityChanged) {
      this.onFilterClear();
      this.getList();
      this.startSearching();
      this.resetParentEntityChanged.emit(false);
    }

    // To edit sub organizations of an added group organization
    if (
      this.groupModeEdit &&
      this.organizationGroup &&
      this.selectedSubOrgToEdit &&
      !this.removeOne
    ) {
      this.getValues();
      this.getList();
      this.startSearching();
      this.selectedSubOrgToEdit?.forEach((subOrgToEdit) => {
        this.localPreSelectedEntities.set(subOrgToEdit.entityUid, {
          id: subOrgToEdit.entityUid,
          text: subOrgToEdit.name,
        });
      });

      if (this.localPreSelectedEntities) {
        // Entities adding a new organization group
        this.allPreSelectedEntities = cloneDeep(this.localPreSelectedEntities);
        this.setAllPreSelectedEntityValues();
        if (this.allPreSelectedEntities?.size) {
          this.setControlValues();
        }
      }

      this.localPreSelectedEntities?.forEach((selected) => {
        if (!this.treeState?.selectedLeafNodeIds) {
          this.treeState = {
            ...this.treeState,
            selectedLeafNodeIds: { ...this.treeState?.selectedLeafNodeIds, [selected.id]: true },
          };
        } else {
          this.treeState.selectedLeafNodeIds = {
            ...this.treeState.selectedLeafNodeIds,
            [selected.id]: true,
          };
        }
      });
      this.tree?.treeModel?.setState(this.treeState);
      this.tree?.treeModel?.update();
      this.tree?.sizeChanged();
    }

    this.updateText();
    if (changes.selectedEntities) {
      this.includeDeleted = this.allowDeleted && this.preselectedIncludeDeleted;
      this.setCurrentRootEntity();
      this.initEntities();
      this.isActive = true;
    }
  }

  setCurrentRootEntity(): void {
    this.currentRootEntity = this.selectedParentEntity?.entityUid || this.authService.entityUid;
  }

  ngOnInit(): void {
    this.setCurrentRootEntity();
    this.isFriendlyNameFeatureActive = this.featureToggleService.isConfigFeatureActive(
      Features.friendlyName,
    );
    if (this.editSchedulerHierarchyFilter) {
      this.startSearching();
      this.getValues();
      this.getList();
      this.initEntities();
    } else {
      this.initData();
    }

    if (this.isSubMenuFilter) {
      this.isActive = false;
    }
    this.backToMenuSub = this.backToMenuSubject$.pipe(debounceTime(100)).subscribe(() => {
      const element = document.getElementById('add-filter');
      if (element) {
        element.click();
      }
    });

    this.itemToggled.pipe(untilDestroyed(this)).subscribe(() => (this._start = 0));
    this.entityRemoved$.pipe(untilDestroyed(this)).subscribe((entityToRemove: ISelectInput) => {
      if (entityToRemove?.id) {
        this.onRemoveSelectedOrganization({ entityUid: entityToRemove.id });
      }
    });

    this.filtersService.actionRemoveFilter$.subscribe((entityToRemove: ISelectInput) => {
      if (SearchSelector.EntityId === entityToRemove.key) {
        const selectedEntity = this.localPreSelectedEntities.get(entityToRemove.id);
        if (selectedEntity) {
          this.onRemoveSelectedOrganization({ entityUid: entityToRemove.id });
          this.itemToggled.emit();
        }
      }
    });

    this.getEntityById(this.currentRootEntity)?.subscribe((entity) => {
      this.rootParentNodeData = cloneDeep(entity);
    });

    this.disableOrEnableUseCheckbox();

    this.resetEntity$.subscribe(() => {
      this.resetInputValue();
    });

    if (
      this.groupModeEdit &&
      this.organizationGroup &&
      this.selectedSubOrgToEdit &&
      !this.removeOne
    ) {
      this.entityListGroupOrganisation = cloneDeep(this.selectedSubOrgToEdit);
      this.entityIdListGroupOrganisation = this.entityListGroupOrganisation.map(
        (entity) => entity.entityUid,
      );
    }

    this.isDeviceMobile$?.subscribe((isMobile) => {
      this.isMobile = isMobile;
    });
  }

  setControlValues(): void {
    this.isParentSelectedEmitter.emit(false);
    const selectedEntityUids = [];
    Array.from(this.localPreSelectedEntities.values()).forEach((value) => {
      // Exclude the Parent entity from form (add new organization group)
      if (this.selectedParentEntity?.entityUid !== value.id) {
        selectedEntityUids.push(value.id);
      }
    });
    if (this.localPreSelectedEntities.size === SINGLE_ENTITY) {
      if (
        this.selectedParentEntity?.entityUid ===
        this.localPreSelectedEntities.values().next().value.id
      ) {
        this.isParentSelectedEmitter.emit(true);
        return;
      }
    }
    this.controlEntityUids.setValue(selectedEntityUids);
    this.allPreSelectedEntitiesNumber.emit(selectedEntityUids.length);
  }

  ngOnDestroy(): void {
    if (this.backToMenuSub) {
      this.backToMenuSub.unsubscribe();
    }
  }

  backToMenu(): void {
    this.itemToggled.emit();
    this.backToMenuSubject$.next();
    this.isOpen = false;
    this.isActive = false;
    this.isMainDropdownFilter = false;
  }

  openContent(): void {
    this.itemToggled.emit();
    this.isOpen = true;
    this.getList();
  }

  openContentOrganizationGroup(): void {
    this.setCurrentRootEntity();
    if (!this.isOpen && this.organizationGroup) {
      this.isOpen = true;
      this.getValues();
      this.getList();
      this.startSearching();
    }
    this.openDropDown.emit(this.isOpen && this.isMainDropdownFilter && this.isActive);
  }

  updateApplyRemovingEntity(): void {
    const values = new Set<string>();
    Array.from(this.allPreSelectedEntities.values()).forEach((val: ISelectInput) => {
      values.add(val.id);
    });

    if (Array.from(values).length) {
      this.filterApplied.emit({
        selector: this.searchSelector,
        key: this.searchSelector,
        operator: SearchOperator.In,
        value: Array.from(this.allPreSelectedEntities.values()),
        argument: `(${Array.from(Array.from(values)).join(',')})`,
      });
    }
  }

  onClose(): void {
    this.isOpen = false;
    this.openDropDown.emit(!this.isOpen && !this.isMainDropdownFilter && !this.isActive);
  }

  onRemoveSelectedOrganization(selectedEntity: IEntity, isButtonRemovedClicked = false): void {
    this.allPreSelectedEntities.delete(selectedEntity.entityUid);
    this.intermediatePreSelectedEntities.delete(selectedEntity.entityUid);
    this.selectedSubOrgToEdit?.filter((org) => org.entityUid !== selectedEntity.entityUid);
    this.removeOne = true;
    this.selectedEntitiesStored.delete(selectedEntity.entityUid);
    this.onEntityRemoved({ id: selectedEntity.entityUid });
    this.deselected(selectedEntity, '', isButtonRemovedClicked);
    this.updateApplyRemovingEntity();
    this.setControlValues();
    this.setAllPreSelectedEntityValues();
    this.filterUpdate(this.filterUpdated, this.selectedAncestorsFilterUpdated, 1);
    if (selectedEntity?.hasChildren && selectedEntity.children?.length) {
      selectedEntity.children.forEach((child) => {
        this.onRemoveSelectedOrganization(child);
      });
    }
  }

  getEntitiesByName(term: string, noLoader?: boolean): Observable<IOrganisation[]> {
    const reachedTheEnd = (val: IOrganisation[]): boolean => {
      return (this.stopLoading = val.length < 19);
    };
    this.isOrgExist = true;
    if (this.entityType !== '') {
      return term
        ? this.searchParameterType === SearchParameters.EntityUid
          ? this.organisationService.getByKey(term).pipe(map((value) => [value]))
          : this.organisationService
              .getLightWeightWithQuery(
                {
                  [this.searchParameterType]: term,
                  entityType: this.entityType,
                },
                noLoader,
                this._start,
                this.limitSelectedEntityElements,
                true,
                this.isEntityTypeGroup,
              )
              .pipe(tap(reachedTheEnd))
        : this.getEntity(this._start, noLoader, reachedTheEnd);
    }

    return term
      ? this.searchParameterType === SearchParameters.EntityUid
        ? this.organisationService.getByKeyWithAncestors(term, false, null, false).pipe(
            map((value) => {
              if (value) {
                return [value];
              }
              this.isOrgExist = false;
              return [];
            }),
            catchError((err) => {
              if (err.error.status === HttpError.CodeError404) {
                this.isOrgExist = false;
                return of([]);
              }
            }),
          )
        : this.organisationService
            .getLightWeightWithQuery(
              {
                [this.searchParameterType]: term,
              },
              noLoader,
              this._start,
              this.limitSelectedEntityElements,
              true,
              this.isEntityTypeGroup,
            )
            .pipe(tap(reachedTheEnd))
      : this.getEntity(this._start, noLoader, reachedTheEnd);
  }

  loadMore(node: TreeNode): void {
    if (node.data.children.length >= node.data.count) {
      this.isLastBatch = true;
      return;
    }
    if (node.data.count - node.data.children.length <= this.limitSelectedEntityElements) {
      this.isLastBatch = true;
    }
    this.loadingMore$.next(node.id);
    const currentScrollPosition = document.querySelector('tree-viewport')?.scrollTop || 0;
    this.organisationService
      .getHierarchyView(
        node.data.entityUid,
        node.data.children.length === 1 ? 0 : node.data.children.length,
        {},
        String(this.limitSelectedEntityElements),
      )
      .subscribe(
        (entities) => {
          if (entities?.length) {
            this.setLocalExpandedHierarchyList(entities);
            if (this.tree?.treeModel?.getState()?.selectedLeafNodeIds[node.data.entityUid]) {
              entities.forEach((entity) => {
                this.treeState.selectedLeafNodeIds = {
                  ...this.treeState.selectedLeafNodeIds,
                  [entity.entityUid]: false,
                };
              });
              this.tree?.treeModel?.setState(this.treeState);
            }
          }
          node.data.children.push(
            ...(this.selectedNodes?.length
              ? entities.filter((entity) => !this.selectedPath?.includes(entity.entityUid))
              : entities),
          );
          this.loadingMore$.next(0);
          this.tree?.treeModel?.update();
          this.tree?.sizeChanged();
          setTimeout(() => {
            const scrollElement = document.querySelector('tree-viewport');
            if (scrollElement) {
              scrollElement.scrollTop = currentScrollPosition;
            }
          }, 0);
        },
        () => {},
        () => {
          this.loadingMore$.next(0);
        },
      );
  }

  updateEntities(): void {
    if (this.selectLimit > 0) {
      this.localPreSelectedEntities = new Map(
        [...this.localPreSelectedEntities.entries()].slice(0, this.selectLimit),
      );
    }

    this.showSelectedEntities = cloneDeep(this.localPreSelectedEntities);
    Array.from(this.localPreSelectedEntities?.values()).forEach((value) => {
      if (this.treeState?.selectedLeafNodeIds) {
        this.treeState.selectedLeafNodeIds[value.id] = true;
      }
    });
    this.tree?.treeModel?.setState(this.treeState);
    this.entities = this.entitiesAll;
    // TODO review it why does not return all the Entities
    this.allEntities = this.entitiesAll?.map((entity) => {
      return {
        id: entity.entityUid,
        text: entity.name,
      };
    });
    this.listAllEntities.emit(this.allEntities);
    this.organisationTextName.emit(FilterSubMenu.Organisation);
  }

  setSelectedEntities(value: ISearchCriteria): void {
    // Set selectedFilters
    const values = new Set<string>(value.argument?.replace('(', '')?.replace(')', '')?.split(','));
    const selectedForState = {};
    forEach(Array.from(values), (argument: string) => {
      selectedForState[argument] = true;
      const node = this.tree?.treeModel.getNodeById(argument);
      this.localPreSelectedEntities.set(argument, {
        id: argument,
        text: node?.data?.name,
      });
    });
    this.treeState = this.treeState
      ? {
          ...this.treeState,
          selectedLeafNodeIds: { ...this.treeState.selectedLeafNodeIds, ...selectedForState },
        }
      : { selectedLeafNodeIds: selectedForState };
    this.updateSelectedEntities();
  }

  startListeningForSearch(): void {
    this.search$ = this.search(this.searchTerm$);
    this.search$.pipe(untilDestroyed(this)).subscribe((data) => {
      if (!data?.length) {
        this.isOrgExist = false;
      }
      this.entities = data;
      this.entitiesAll = data;
      if (this.selectedEntityList.size) {
        this.updateEntities();
        this.selectedPath = undefined;
        this.selectedNodes = undefined;
        Array.from(this.selectedEntityList.values()).forEach((value) => {
          this.treeState.selectedLeafNodeIds[value.data.entityUid] = true;
          const id = value.data.entityUid;
          if (id !== this.currentRootEntity && !this.selectedPath?.includes(id)) {
            this.organisationService
              .getAncestors(id)
              .pipe(
                catchError((err) => {
                  const code = ErrorService.getStatusCode(err);
                  if (code === HttpError.CodeError404) return EMPTY;
                  return throwError(err);
                }),
              )
              .subscribe(
                (ancestors) => {
                  this.flatten(ancestors);
                },
                (error: Object) => {
                  this.notifierService.error(
                    $localize`Error` +
                      ` (${get(error, 'error.status')}) ${get(error, 'error.error.message')}`,
                  );
                },
              );
          }
        });
      }
    });
  }

  startSearching(): void {
    this.search$ = this.search(this.searchTerm$);
    this.search$.pipe(untilDestroyed(this)).subscribe((data) => {
      this.entities = data;
      this.entitiesAll = data;
      if (this.selectedEntityList.size) {
        this.updateEntities();
        this.selectedPath = undefined;
        this.selectedNodes = undefined;
        Array.from(this.selectedEntityList.values()).forEach((value) => {
          this.treeState.selectedLeafNodeIds[value.data.entityUid] = true;
        });
      }
    });
  }

  updateSelectedEntities(): void {
    this.showSelectedEntities = cloneDeep(this.localPreSelectedEntities);
    const entitiesToFetch: string[] = [];
    Array.from(this.showSelectedEntities.values()).forEach((val: ISelectInput) => {
      if (!val.text) {
        entitiesToFetch.push(val.id);
      }
    });

    if (entitiesToFetch.length) {
      forkJoin(
        entitiesToFetch.map((id: string) => {
          return this.getEntityById(id);
        }),
      )
        .pipe(untilDestroyed(this))
        .subscribe((orgs: IOrganisation[]) => {
          forEach(orgs, (org: IOrganisation) => {
            const input = this.showSelectedEntities.get(org.entityUid);
            if (input) {
              input.text = org.name;
            }
            this.localPreSelectedEntities.set(org.entityUid, {
              id: org.entityUid,
              text: org.name,
            });
          });
          if (this.isScheduler) {
            const values: string[] = this.getValues();
            this.filterUpdated.emit({
              key: this.searchSelector,
              selector: this.searchSelector,
              operator: SearchOperator.In,
              value: Array.from(this.localPreSelectedEntities.values()),
              argument: `(${Array.from(values).join(',')})`,
            });
          } else {
            this.delayFilterUpdate();
          }
          this.updateText();
          this.attachScrollEvent();
        });
    } else {
      if (this.treeState?.selectedLeafNodeIds) {
        this.treeState.selectedLeafNodeIds = {};
        this.tree?.treeModel?.setState(this.treeState);
      }
    }
  }

  onStatusChange(): void {
    this.entitiesAll = this.entities = [];
    this._start = 0;
    this.tree?.treeModel.update();

    const isPortionLoader = false;

    this.organisationService
      .getEntity(this._start, isPortionLoader, this.authService.entityUid)
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.entities = data;
        this.entitiesAll = data;
        if (this.localPreSelectedEntities.size) {
          this.updateEntities();
          this.selectedPath = undefined;
          this.selectedNodes = undefined;
          Array.from(this.localPreSelectedEntities.values()).forEach((value) => {
            this.treeState.selectedLeafNodeIds[value.id] = true;
          });
        }
      });

    this.initEntities();
    if (this.searchedText) {
      this.searchTerm$.next('');
      this.searchTerm$.next(this.searchedText);
    }
  }

  onSearch(searchValue = ''): void {
    this.searchedText = searchValue;
    this._start = 0;
    this.searchTerm$.next(searchValue);
  }

  search(terms: Observable<string>): Observable<IOrganisation[]> {
    return terms.pipe(distinctUntilChanged(), debounceTime(this.debounce)).pipe(
      switchMap((term: string) => {
        // Always reset debounce time
        if (this.treeState) {
          this.treeState.expandedNodeIds = {};
          this.treeState.selectedLeafNodeIds = {};
          this.tree?.treeModel?.update();
        }
        this.debounce = FilterEntityComponent.initialDebounce;
        return this.getEntitiesByName(term);
      }),
      tap(() => this.tree?.sizeChanged()),
    );
  }

  clear(): void {
    if (this.isOpen) {
      this.itemToggled.emit();
    }
    this.isOpen = false;
    this.removeAllEntities();
  }

  getEntityById(id: string): Observable<IOrganisation> {
    return this.organisationService.getByKey(id);
  }

  onFilterApplied(): void {
    this.localPreSelectedEntities.clear();
    this.intermediatePreSelectedEntities.forEach((entity) => {
      this.localPreSelectedEntities.set(entity.id, entity);
    });
    this.filterUpdate(this.filterUpdated, this.selectedAncestorsFilterUpdated, 0);
    this.itemToggled.emit();
    this.isOpen = false;
    if (this.isSubMenuFilter) {
      this.isActive = false;
      this.isMainDropdownFilter = false;
    }
    this.filterAppliedSelectedEntities.emit(this.localPreSelectedEntities);
    this.openDropDown.emit(!this.isOpen && !this.isMainDropdownFilter && !this.isActive);
  }

  resetInputValue(): void {
    this.onSearch('');
    this.searchParameterType = SearchParameters.Name;
    this.setAddButtonLabel();
  }

  onFilterClear(): void {
    this.removeAllEntities();
    this.resetInputValue();
    this.delayFilterUpdate();
    this.resetFocus();
    this.entityIdListGroupOrganisation = [];
    this.flatternEntityListWithChildren = [];
  }

  removeAllEntities(): void {
    this.localPreSelectedEntities.clear();
    this.localPreSelectedAncestorEntities.clear();
    this.selectedEntityList.clear();
    this.numberOfSelectedEntities = null;
    this.updateSelectedEntities();
    this.updateText();
    this.resetArrowControls();
  }

  onEntityRemoved(selectedEntity: ISelectInput): void {
    this.localPreSelectedEntities.delete(selectedEntity.id);
  }

  onEntityAdded(selectedEntity: ISelectInput): void {
    if (selectedEntity?.id) {
      this.localPreSelectedEntities.set(selectedEntity.id, {
        id: selectedEntity.id,
        text: this.tree?.treeModel.getNodeById(selectedEntity.id).data?.name,
      });
    }
  }

  getList(): Observable<IOrganisation[]> {
    this.searchedText = '';
    this.searchInput?.reset();
    this.synchronizeLocalPreSelectedEntities();
    this.debounce = 0;
    this.searchTerm$.next(this.searchedText);
    this.attachScrollEvent();
    return null;
  }

  groupEntityWillBeNotPreSelected(): void {
    if (
      this.localPreSelectedEntities.get(this.authorizationService['user']?.entity?.entityUid) &&
      this.authorizationService['user'].entity.entityType === EntityTypeEnum.GROUP
    ) {
      this.localPreSelectedEntities.delete(this.authorizationService['user']?.entity?.entityUid);
    }
  }

  getValues(): string[] {
    const values = new Set<string>();
    this.groupEntityWillBeNotPreSelected();
    Array.from(this.localPreSelectedEntities.values()).forEach((val: ISelectInput) => {
      values.add(val.id);
    });
    return Array.from(values);
  }

  groupEntityWillBeNotAncestorPreSelected(): void {
    if (
      this.localPreSelectedAncestorEntities.get(
        this.authorizationService['user']?.entity?.entityUid,
      ) &&
      this.authorizationService['user'].entity.entityType === EntityTypeEnum.GROUP
    ) {
      this.localPreSelectedAncestorEntities.delete(
        this.authorizationService['user']?.entity?.entityUid,
      );
    }
  }

  getSelectedAncestorValues(): string[] {
    const values = new Set<string>();
    this.groupEntityWillBeNotAncestorPreSelected();
    Array.from(this.localPreSelectedAncestorEntities.values()).forEach((val: ISelectInput) => {
      values.add(val.id);
    });
    return Array.from(values);
  }

  updateText(): void {
    const values: string[] = this.getValues();
    // important not to change this line
    const text = this.entityText;
    if (values.length || this.numberOfSelectedEntities) {
      this.title = `${text} (+${this.numberOfSelectedEntities || values.length})`;
      this.selectedEntityCount = this.numberOfSelectedEntities || values.length;
    } else {
      this.title = text;
      this.selectedEntityCount = 0;
    }
  }

  get limitExceeded(): boolean {
    return this.selectLimit > 0 && this.localPreSelectedEntities.size > this.selectLimit;
  }

  deselected(event: any, deselectedEntityId = '', isButtonRemovedClicked = false): void {
    this.deselectNode = { click: true, event: event };

    this.numberOfSelectedEntities = null;
    if (event) {
      const entityId =
        event?.node?.data?.entityUid || event?.data?.entityUid || event?.entityUid || event?.id;
      if (this.treeState?.selectedLeafNodeIds[entityId] || isButtonRemovedClicked) {
        this.treeState.selectedLeafNodeIds = {
          ...this.treeState.selectedLeafNodeIds,
          [entityId]: false,
        };
        this.selectedEntityList.delete(entityId);
        if (this.organizationGroup) {
          this.removeEntityListGroupOrganisation(event, deselectedEntityId, isButtonRemovedClicked);
        }
      }
      if (event?.node?.parent || event?.parent) {
        this.deselected(
          event?.node?.parent || event?.parent,
          event?.node?.data?.entityUid || event?.data?.entityUid || event?.entityUid || event?.id,
        );
      }
      this.intermediatePreSelectedEntities.delete(entityId);
    }
    this.tree?.treeModel?.setState(this.treeState);
    if (event?.data?.entityUid) {
      this.selectedEntitiesStored.delete(event.data.entityUid);
    }
    if (this.getFilteredNodeCount() > this.limitSelectedEntityElements) {
      this.notificationLimitElements();
      this.disabledApply = true;
      this.isExceededSelectedAncestors.emit(this.disabledApply);
    } else {
      this.disabledApply = false;
      this.isExceededSelectedAncestors.emit(this.disabledApply);
    }
    if (!this.selectedEntityList.size) {
      this.resetArrowControls();
    }
    this.setAddButtonLabel();
  }

  deselectSelectedEntity(event): void {
    const node = this.tree?.treeModel?.getNodeById(event?.data?.entityUid);
    const entityUid = event?.data?.entityUid || event?.entityUid || event?.id;
    const ids = [];
    const removeChildren = (children): void => {
      children?.forEach((child) => {
        this.selectedEntityList.delete(child.entityUid);
        ids.push(child.entityUid);
        if (child.hasChildren) {
          removeChildren(child.children);
        }
      });
    };
    const removeParentIfChildrenNotSelected = (parent): void => {
      if (
        this.selectedEntityList.has(parent.data.entityUid) &&
        typeof parent?.data?.entityUid === 'string' &&
        parent?.data?.hasChildren
      ) {
        const allChildrenNotSelected = parent.data.children.every(
          (child) => !this.selectedEntityList.has(child.entityUid),
        );
        if (allChildrenNotSelected) {
          this.selectedEntityList.delete(parent.data.entityUid);
          ids.push(parent.data.entityUid);
          if (parent.parent) {
            removeParentIfChildrenNotSelected(parent.parent);
          }
        }
      }
    };
    if (node) {
      node.setIsSelected(false);
    } else {
      this.selectedEntityList.delete(entityUid);
      this.localPreSelectedEntities.delete(entityUid);
      ids.push(entityUid);
      removeChildren(event.data.children);
      removeParentIfChildrenNotSelected(event.parent);
      this.selectedEntityList.forEach((value, key) => {
        let currentParent = value?.data?.parent || value?.parent;
        while (currentParent) {
          if (
            ids.includes(currentParent.entityUid) ||
            ids.includes(currentParent.data?.entityUid)
          ) {
            this.selectedEntityList.delete(key);
            this.localPreSelectedEntities.delete(entityUid);
            ids.push(value.data.entityUid);
            break;
          }
          currentParent = currentParent.parent;
        }
      });
      ids.forEach((id) => {
        if (this.treeState?.selectedLeafNodeIds[id]) {
          this.treeState.selectedLeafNodeIds[id] = false;
        }
      });
      this.tree?.treeModel?.setState(this.treeState);
    }
    if (!this.selectedEntityList.size) {
      this.resetArrowControls();
    }
    this.setAddButtonLabel();
  }

  selected(event: any): void {
    if (event?.data?.entityUid && event?.data?.name) {
      if (!this.selectedEntitiesStored.get(event.data.entityUid)) {
        this.selectedEntitiesStored.set(event.data.entityUid, event.data.name);
      }
      this.selectedEntityList.set(event.data.entityUid, {
        ...this.getSelectedEntityHierarchy(event),
        path: event.data.path?.trim() || this.transform(event).slice(0, -4),
      });
      this.intermediateselectedEntityList = this.selectedEntityList;

      if (this.organizationGroup) {
        this.setEntityListGroupOrganisation(event);
      }
      this.intermediatePreSelectedEntities.set(event?.data?.entityUid, {
        id: event?.data?.entityUid,
        text: event?.data?.name,
      });
    }

    this.selectNode = { click: true, event: event };

    this.numberOfSelectedEntities = null;
    // setTimeout is as tree updates are slow
    setTimeout(() => {
      this.setAddButtonLabel();
      if (this.getFilteredNodeCount() > this.limitSelectedEntityElements) {
        this.notificationLimitElements();
        this.disabledApply = true;
        this.isExceededSelectedAncestors.emit(this.disabledApply);
      } else {
        this.disabledApply = false;
        this.isExceededSelectedAncestors.emit(this.disabledApply);
      }
      if (event?.parent && typeof event.parent.data.entityUid === 'string') {
        if (
          event.parent.data.children.find(
            (val) => !this.tree?.treeModel?.getState().selectedLeafNodeIds[val.entityUid],
          )
        ) {
          return;
        }
        if (this.treeState) {
          this.treeState.selectedLeafNodeIds[event.parent.data.entityUid] = true;
          this.tree?.treeModel?.setState(this.treeState);
          // recursion to update till the root
          this.selected(event.parent);
        }
      } else {
        // The root entity not selected
        if (event?.data?.entityUid) {
          if (this.treeState) {
            this.treeState.selectedLeafNodeIds[event.data.entityUid] = true;
            this.tree?.treeModel?.setState(this.treeState);
            this.tree?.treeModel?.update();
            this.tree?.sizeChanged();
          }
        }
      }
    });
  }

  transform(entity: any, path = ''): string {
    if (entity.parent && entity.data.entityUid !== this.authService.entityUid) {
      return this.transform(entity.parent, `${path && entity.data.name} ${path && '/ ' + path}`);
    } else {
      return `${entity.data.name} / ${path}`;
    }
  }

  getSelectedEntityHierarchy(entity): any {
    if (!entity) {
      return null;
    }
    const { data, parent } = entity;
    const sanitizedParent = parent ? { data: parent.data, parent: parent.parent } : null;

    return {
      data,
      parent: sanitizedParent ? this.getSelectedEntityHierarchy(sanitizedParent) : null,
    };
  }

  setEntityListGroupOrganisation(event): void {
    if (!this.entityIdListGroupOrganisation.includes(event.data.parentEntityUid)) {
      this.entityIdListGroupOrganisation.push(event.data.entityUid);
      this.entityListGroupOrganisation.push(event.data);
    }
    this.entityIdListGroupOrganisation = [...new Set(this.entityIdListGroupOrganisation)];
    this.entityListGroupOrganisation = Array.from(
      this.entityListGroupOrganisation
        .reduce((map1, obj) => map1.set(obj.entityUid, obj), new Map())
        .values(),
    );
    if (event.data.hasChildren && event.data.children?.length) {
      this.organisationService
        .getLightWeightWithQuery(
          { parentId: event.data.entityUid },
          false,
          0,
          event?.node?.data?.count || event?.data?.count || event?.count,
        )
        .subscribe((childEntities) => {
          childEntities?.forEach((child) => {
            const index = this.entityIdListGroupOrganisation.indexOf(child.entityUid);
            const objIndex = this.entityListGroupOrganisation.findIndex(
              (entity) => entity.entityUid === child.entityUid,
            );
            if (index > -1) {
              this.entityIdListGroupOrganisation.splice(index, 1);
              this.entityListGroupOrganisation.splice(objIndex, 1);
            }
          });
        });
    }
  }

  getEntityListWithChildrenGroupOrganisation(data): void {
    const index = this.flatternEntityListWithChildren.findIndex(
      (entity) => entity.entityUid === data.entityUid,
    );
    if (index < 0) {
      this.flatternEntityListWithChildren.push({ ...data, id: data.entityUid });
    }
    if (data.hasChildren) {
      if (data.children?.length) {
        const allPresent = data.children.every((a) =>
          this.flatternEntityListWithChildren.some((b) => b.entityUid === a.entityUid),
        );
        if (allPresent && data.count === data.children.length) return;
      }
      this.organisationService
        .getHierarchyView(data.entityUid, 0, {}, String(data.count))
        .subscribe((childEntities) => {
          if (childEntities?.length) {
            childEntities.forEach((childEntity) => {
              const i = this.flatternEntityListWithChildren.findIndex(
                (entity) => entity.entityUid === childEntity.entityUid,
              );
              if (i < 0) {
                this.flatternEntityListWithChildren.push({
                  ...childEntity,
                  id: childEntity.entityUid,
                });
                if (childEntity.hasChildren) {
                  this.getEntityListWithChildrenGroupOrganisation(childEntity);
                }
              }
            });
          }
        });
    }
  }

  removeEntityListGroupOrganisation(event, deselectedChildEntityId, isButtonRemovedClicked): void {
    const entityId =
      event?.node?.data?.entityUid || event?.data?.entityUid || event?.entityUid || event?.id;

    if (
      !(
        Object.keys(this.treeState.selectedLeafNodeIds).includes(entityId) || isButtonRemovedClicked
      )
    ) {
      return;
    }

    // Remove the entityId if it exists in the list
    const index = this.entityIdListGroupOrganisation.indexOf(entityId);
    const objIndex = this.entityListGroupOrganisation.findIndex(
      (entity) => entity.entityUid === entityId,
    );
    if (index > -1) {
      this.entityIdListGroupOrganisation.splice(index, 1);
      this.entityListGroupOrganisation.splice(objIndex, 1);
    }
    if (isButtonRemovedClicked) {
      this.onFilterApplied();
    }

    // Check if the entity has children and select all children excluding deselected entity
    this.organisationService
      .hasChildren({ entityUid: entityId })
      .pipe(
        filter((hasChildren) => hasChildren && deselectedChildEntityId), // Proceed only if true
        switchMap(() =>
          this.organisationService.getLightWeightWithQuery(
            { parentId: entityId },
            false,
            0,
            event?.node?.data?.count || event?.data?.count || event?.count,
          ),
        ),
      )
      .subscribe((childEntities) => {
        if (childEntities?.length) {
          const filteredEntities = childEntities.filter(
            (entity) => entity.entityUid !== deselectedChildEntityId,
          );

          this.entityIdListGroupOrganisation = [
            ...this.entityIdListGroupOrganisation,
            ...filteredEntities.map((entity) => entity.entityUid),
          ];
          forkJoin(
            filteredEntities.map((val) =>
              this.organisationService.getTotalCount({
                parentId: val[SearchSelector.EntityUid],
              }),
            ),
          )
            .pipe(
              map((val) =>
                val.map((totalCountData, i) => ({
                  ...filteredEntities[i],
                  id: filteredEntities[i].entityUid,
                  count: totalCountData.count,
                  hasChildren: totalCountData.count > 0,
                })),
              ),
            )
            .subscribe((result) => {
              this.entityListGroupOrganisation = [...this.entityListGroupOrganisation, ...result];
            });
        }
      });
  }

  removeEntityListWithChildrenGroupOrganisation(data): void {
    const parentIndex = this.flatternEntityListWithChildren.findIndex(
      (entity) => entity.entityUid === data.parentEntityUid,
    );
    if (parentIndex > -1) {
      this.flatternEntityListWithChildren.splice(parentIndex, 1);
    }
    const index = this.flatternEntityListWithChildren.findIndex(
      (entity) => entity.entityUid === data.entityUid,
    );
    if (index > -1) {
      this.flatternEntityListWithChildren.splice(index, 1);
      if (data.hasChildren) {
        if (data.children?.length) {
          const allPresent = data.children.every((a) =>
            this.flatternEntityListWithChildren.some((b) => b.entityUid === a.entityUid),
          );
          if (allPresent && data.count === data.children.length) {
            data.children.forEach((child) => {
              const i = this.flatternEntityListWithChildren.findIndex(
                (entity) => entity.entityUid === child.entityUid,
              );
              if (i > -1) {
                this.flatternEntityListWithChildren.splice(i, 1);
              }
            });
            return;
          }
        }
        this.organisationService
          .getHierarchyView(data.entityUid, 0, {}, String(data.count))
          .subscribe((childEntities) => {
            if (childEntities?.length) {
              childEntities.forEach((childEntity) => {
                const i = this.flatternEntityListWithChildren.findIndex(
                  (entity) => entity.entityUid === childEntity.entityUid,
                );
                if (i > -1) {
                  if (childEntity.hasChildren) {
                    this.removeEntityListWithChildrenGroupOrganisation(childEntity);
                  } else {
                    this.flatternEntityListWithChildren.splice(i, 1);
                  }
                }
              });
            }
          });
      }
    }
  }

  insertNodeInSelectedAncestorEntities(entityUid: string): void {
    if (!this.localPreSelectedAncestorEntities.get(entityUid)) {
      this.localPreSelectedAncestorEntities.set(entityUid, {
        id: entityUid,
        text: this.getNodeByEntityUid(entityUid)?.name,
      });
    }
  }

  removeSelectedNodesFromAncestorEntities(
    parentEntityUid: string,
    ancestorSelectedEntities: string[],
  ): void {
    ancestorSelectedEntities.forEach((nodeId) => {
      const node = this.getNodeByEntityUid(nodeId);
      if (node && node.parentEntityUid === parentEntityUid) {
        if (this.localPreSelectedAncestorEntities?.get(nodeId)) {
          this.localPreSelectedAncestorEntities.delete(nodeId);
        }
      }
    });
  }

  getCountedSelectedNodesFromParent(
    parentEntityUid: string,
    ancestorSelectedEntities: string[],
  ): number {
    let cnt = 0;
    ancestorSelectedEntities.forEach((nodeId) => {
      if (this.getNodeByEntityUid(nodeId)?.parentEntityUid === parentEntityUid) {
        cnt++;
      }
    });
    return cnt;
  }

  filterSetAncestorEntities(): void {
    this.localPreSelectedAncestorEntities = new Map<string, ISelectInput>();
    this.localPreSelectedAncestorEntities = cloneDeep(this.localPreSelectedEntities);
    this.updateText();
    if (
      !this.selectNode?.event?.data?.parentEntityUid &&
      !this.selectNode?.event?.data?.parent &&
      this.selectNode?.event?.data?.entityUid === this.currentRootEntity &&
      this.getValues()?.length === 1
    ) {
      this.localPreSelectedAncestorEntities.set(this.currentRootEntity, {
        id: this.currentRootEntity,
        text:
          this.rootParentNodeData?.name ||
          this.tree?.treeModel?.getNodeById(this.currentRootEntity)?.data?.name ||
          this.getPreSelectedEntityName(this.currentRootEntity),
      });
    } else {
      // If the root entity in, removing it
      if (
        this.localPreSelectedEntities.get(this.currentRootEntity) &&
        this.getValues()?.length > 1
      ) {
        this.localPreSelectedEntities.delete(this.currentRootEntity);
      }

      this.localPreSelectedAncestorEntities = cloneDeep(this.localPreSelectedEntities);
      const values = new Set<string>();
      Array.from(this.localPreSelectedAncestorEntities.values()).forEach((val: ISelectInput) => {
        values.add(val.id);
      });
      this.updateText();

      const ancestorEntities = Array.from(values);
      let countNodesFromParent = 0,
        countingSelectedExpandedNodes = 0;
      ancestorEntities.forEach((nodeId) => {
        const selectedEntity = this.getNodeByEntityUid(nodeId);
        if (selectedEntity?.parentEntityUid) {
          // parent node
          const parentNode = this.getNodeByEntityUid(
            this.getNodeByEntityUid(nodeId).parentEntityUid,
          );
          if (parentNode) {
            countNodesFromParent = parentNode.count;
          }
          countingSelectedExpandedNodes = this.getCountedSelectedNodesFromParent(
            this.getNodeByEntityUid(nodeId).parentEntityUid,
            ancestorEntities,
          );

          // Remove the selected expanded nodes, and insert the parent
          if (countingSelectedExpandedNodes >= countNodesFromParent) {
            this.removeSelectedNodesFromAncestorEntities(
              this.getNodeByEntityUid(nodeId).parentEntityUid,
              ancestorEntities,
            );
            this.insertNodeInSelectedAncestorEntities(
              this.getNodeByEntityUid(nodeId).parentEntityUid,
            );
          }
        }
      });
    }

    if (this.isPreSelectedAncestorIds) {
      this.ancestorEntitiesElements = this.localPreSelectedAncestorEntities?.size || 0;
      if (this.selectNode?.click || this.deselectNode?.click) {
        this.delayFilterUpdate();
        this.selectNode = { click: false, event: this.selectNode?.event || null };
        this.deselectNode = { click: false, event: this.deselectNode?.event || null };
      }
      // we don't emit
      if (this.getValues()?.length > this.limitSelectedEntityElements) {
        this.localPreSelectedAncestorEntities = cloneDeep(this.localPreSelectedEntities);
      }

      this.updateText();
      this.disableOrEnableUseCheckbox();
    }
    this.selectedAncestorEntitiesNumber.emit(this.localPreSelectedAncestorEntities?.size || 0);
  }

  delayFilterUpdate(): void {
    setTimeout(() => {
      this.filterUpdate(this.filterUpdated, this.selectedAncestorsFilterUpdated, 0);
    }, 0);
  }

  getPreSelectedEntityName(entity: string): string {
    return this.selectedEntitiesStored?.has(entity) ? this.selectedEntitiesStored?.get(entity) : '';
  }

  checkTabPress(event): void {
    if ((event.target as HTMLElement)?.id === 'orgClearButton') {
      event.preventDefault();
      this.onFilterClear();
    }
  }

  onKeyDownApply(): void {
    const body = document.querySelector('body');
    body.addEventListener('keyup', this.checkTabPress);
  }

  onFocusIn(event: FocusEvent, treeModel: TreeModel): void {
    const treeElement = event.currentTarget as HTMLElement;

    if (event.target === treeElement) {
      treeModel.setFocus(true);
      if (!treeModel.focusedNode) {
        treeModel.getFirstRoot()?.focus();
      }
    } else {
      // prevent inputs inside the tree stealing the focus
      treeElement.focus();
    }
  }

  onFocusOut(event: FocusEvent, treeModel: TreeModel): void {
    if (event.target === event.currentTarget) {
      treeModel.setFocus(false);
    }
  }

  toggleCheckbox(node: TreeNode): void {
    const newNodeSelectedState = !node.isSelected;
    this.updateNodeAndDescendants(node, newNodeSelectedState);
    setTimeout(() => {
      this.saveScrollPosition();
      node.setIsSelected(newNodeSelectedState);
      this.restoreScrollPosition();
      this.updateText();
    }, 0);
  }

  closeFilter(): void {
    if (this.isOpen) this.isOpen = false;
  }

  clearFilter(): void {
    this.selectedEntityList.clear();
    this.treeState.selectedLeafNodeIds = {};
    this.tree?.treeModel?.setState(this.treeState);
    this.setAddButtonLabel();
    this.resetArrowControls();
  }

  saveScrollPosition(): void {
    const treeContainer = document.querySelector('tree-viewport') as HTMLElement;
    this.scrollPosition = treeContainer?.scrollTop || 0;
  }

  restoreScrollPosition(): void {
    setTimeout(() => {
      const treeContainer = document.querySelector('tree-viewport') as HTMLElement;
      if (treeContainer) {
        treeContainer.scrollTop = this.scrollPosition;
      }
    }, 0); // Delay ensures it runs after DOM updates
  }

  getSelectedEntityText(entity: IEntity): string {
    const entityName = entity?.name;
    const entityType = this.readableEntityTypeService.getEntityTypeValue(entity?.entityType);
    const entityText = entityName ? `${entityName}${entityType ? ` (${entityType})` : ''}` : '';
    return entityText || $localize`No name`;
  }

  adjustHeights(direction: 'up' | 'down'): void {
    const delta = (this.WrapperHeight - this.arrowControlsHeight) * 0.25;

    if (direction === 'up' && this.entityFlex > 0) {
      this.entityFlex -= delta;
      this.treeFlex += delta;
      if (this.isUpArrowDisabled && !this.isDownArrowDisabled) {
        this.downArrow?.nativeElement?.focus();
      }
    } else if (direction === 'down' && this.treeFlex > 0) {
      this.entityFlex += delta;
      this.treeFlex -= delta;
      if (this.isDownArrowDisabled && !this.isUpArrowDisabled) {
        this.upArrow?.nativeElement?.focus();
      }
    }
  }

  onArrowKey(direction: 'up' | 'down', event: Event): void {
    const isDisabled = direction === 'up' ? this.isUpArrowDisabled : this.isDownArrowDisabled;
    if (!isDisabled) {
      this.adjustHeights(direction);
      event.preventDefault();
      event.stopPropagation();
    }
  }

  async groupHierarchySelectParent(): Promise<void> {
    const isSingleEntitySelected = this.localPreSelectedEntities.size === 1;
    const selectedParent = this.localPreSelectedEntities.get(
      this.authorizationService['user']?.entity?.entityUid,
    );
    if (isSingleEntitySelected && selectedParent && this.isEntityTypeGroup) {
      const entities = await this.callGetGroupRelationships(
        this.tree.treeModel.getNodeById(selectedParent.id),
      );
      entities?.forEach((entity) => {
        this.localPreSelectedEntities.set(entity.entityUid, {
          id: entity.entityUid,
          text:
            this.tree?.treeModel?.getNodeById(entity.entityUid)?.data?.name ||
            this.getPreSelectedEntityName(entity.entityUid),
        });
        this.treeState.selectedLeafNodeIds = {
          ...this.treeState.selectedLeafNodeIds,
          [entity.entityUid]: true,
        };
      });
      this.tree?.treeModel?.setState(this.treeState);
      this.tree?.treeModel?.update();
      this.tree?.sizeChanged();
    }
  }

  isEntityTypeVisible(node: any, isDeviceMobile: boolean | null): boolean {
    return this.organisationService.isEntityTypeVisible(
      node,
      this.isFriendlyNameFeatureActive,
      isDeviceMobile,
    );
  }

  private updateNodeAndDescendants(node: TreeNode, newNodeSelectedState: boolean): void {
    // Update the node state
    this.saveScrollPosition();
    node.setIsSelected(newNodeSelectedState);
    this.restoreScrollPosition();

    // If the node has children, recursively update their state
    if (node.children) {
      node.children.map((childNode) => {
        this.updateNodeAndDescendants(childNode, newNodeSelectedState);
      });
    }
  }

  private disableOrEnableUseCheckbox(): void {
    if (this.localPreSelectedAncestorEntities.size > this.limitSelectedEntityElements) {
      this.notificationLimitElements();
      this.disabledApply = true;
      this.isExceededSelectedAncestors.emit(this.disabledApply);
    } else {
      this.disabledApply = false;
      this.isExceededSelectedAncestors.emit(this.disabledApply);
    }
  }

  private notificationLimitElements(): void {
    if (!this.disabledApply) {
      this.notifierService.destroyNotification();
      this.notifierService.setDuration(10000);
      this.notifierService.info($localize`You can only select up to 20 organizations.`);
    }
  }

  private async filterUpdate(
    filterEmitEvent?: EventEmitter<IFilterToApply>,
    selectedAncestorsEmitEvent?: EventEmitter<IFilterToApply>,
    state = 0,
  ): Promise<void> {
    if (!state) {
      if (this.tree) {
        this.localPreSelectedEntities.clear();
      }

      if (this.tree?.treeModel?.getState()?.selectedLeafNodeIds) {
        Object.keys(this.tree?.treeModel?.getState()?.selectedLeafNodeIds)?.forEach((entityId) => {
          if (this.treeState?.selectedLeafNodeIds?.[entityId]) {
            this.localPreSelectedEntities.set(entityId, {
              id: entityId,
              text:
                this.tree?.treeModel?.getNodeById(entityId)?.data?.name ||
                this.getPreSelectedEntityName(entityId),
            });
          }
        });
        await this.groupHierarchySelectParent();
      }
    }

    this.updateEntities();
    this.updateText();
    this.filterSetAncestorEntities();
    const values: string[] = this.getValues();
    const selectedAncestorValues: string[] = this.getSelectedAncestorValues();
    if (values.length) {
      filterEmitEvent?.emit({
        selector: this.searchSelector,
        key: this.searchSelector,
        operator: SearchOperator.In,
        value: Array.from(this.localPreSelectedEntities.values()),
        argument: `(${Array.from(values).join(',')})`,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        value: Array.from(this.localPreSelectedEntities.values()),
      });
    } else {
      filterEmitEvent?.emit({
        key: this.searchSelector,
        toRemove: true,
      });
      this.staticFilterUpdated.emit({
        key: this.searchSelector,
        toRemove: true,
      });
    }

    if (selectedAncestorValues.length) {
      selectedAncestorsEmitEvent?.emit({
        selector: this.searchSelector,
        key: this.searchSelector,
        operator: SearchOperator.In,
        value: Array.from(this.localPreSelectedAncestorEntities.values()),
        argument: `(${Array.from(selectedAncestorValues).join(',')})`,
      });
      this.selectedAncestorsSchedulerFilterUpdated.emit({
        key: this.searchSelector,
        value: Array.from(this.localPreSelectedAncestorEntities.values()),
      });
    } else {
      selectedAncestorsEmitEvent?.emit({
        key: this.searchSelector,
        toRemove: true,
      });
      this.selectedAncestorsSchedulerFilterUpdated.emit({
        key: this.searchSelector,
        toRemove: true,
      });
    }

    // Entities adding a new organization group
    this.allPreSelectedEntities = cloneDeep(this.localPreSelectedEntities);
    this.setAllPreSelectedEntityValues();
    if (this.allPreSelectedEntities?.size) {
      this.setControlValues();
    } else {
      this.isParentSelectedEmitter.emit(false);
      this.allPreSelectedEntitiesNumber.emit(0);
    }
  }

  private setAllPreSelectedEntityValues(): void {
    this.allPreSelectedEntityValues = [];
    this.allPreSelectedEntities?.forEach((value) => {
      this.allPreSelectedEntityValues.push({
        id: value.id,
        text: value.text ? value.text : this.getSelectedOrgName(value),
      });
    });
    this.allPreSelectedEntityValues = [...new Set(this.allPreSelectedEntityValues)];
  }

  private async flatten(data: IEntityAncestors): Promise<any> {
    const result = [];
    while (data) {
      result.push(data);
      if (data?.parent?.entityUid === this.currentRootEntity) {
        break;
      }
      data = data.parent;
    }
    result.forEach((value) => {
      delete value.parent;
    });
    this.selectedPath = [...(this.selectedPath || []), ...result.map((value) => value.entityUid)];
    const response = await forkJoin(
      result.map((val) =>
        this.organisationService.getTotalCount({
          parentId: val[SearchSelector.EntityUid],
        }),
      ),
    )
      .pipe(
        map((val) =>
          val.map((totalCountData, index) => ({
            ...result[index],
            count: totalCountData.count,
            hasChildren: totalCountData.count > 0,
            isExpanded: index !== 0,
          })),
        ),
      )
      .toPromise();
    const length = result.length;
    let selectedNode;

    if (this.selectedNodes?.length) {
      const index = response.findIndex((resp) =>
        this.findInsertExistingNode(resp.entityUid, this.selectedNodes),
      );
      const existingNode = index >= 0 ? response[index] : undefined;
      if (existingNode) {
        selectedNode = response[index - 1];
        this.insertChild(selectedNode, response.slice(0, index - 1));
        this.findInsertExistingNode(existingNode.entityUid, this.selectedNodes, selectedNode);
        this.selectedNodes = cloneDeep(this.selectedNodes);
      } else {
        selectedNode = response[length - 1];
        this.insertChild(selectedNode, response);
        this.selectedNodes = [...this.selectedNodes, selectedNode];
      }
    } else {
      selectedNode = response[length - 1];
      this.insertChild(selectedNode, response);
      this.findInsertExistingNode(selectedNode.entityUid, this.selectedNodes);
    }
    this.expandTree();
  }

  private findInsertExistingNode(
    id: string,
    selectedNodes?: SelectedNode[],
    nodeToInsert?: SelectedNode,
  ): SelectedNode {
    if (!selectedNodes) {
      return;
    }
    for (const item of selectedNodes) {
      if (item.entityUid === id) {
        const children = item?.children || [];
        if (nodeToInsert && !children.some((child) => child.entityUid === nodeToInsert.entityUid)) {
          item.children = [...(item?.children || []), nodeToInsert];
        }
        return item;
      } else {
        const found = this.findInsertExistingNode(id, item.children, nodeToInsert);
        if (found) return found;
      }
    }
    return null;
  }

  private insertChild(
    selectedNode: SelectedNode,
    array: SelectedNode[],
    index: number = array.length - 2,
  ): void {
    if (index >= 0 && selectedNode) {
      selectedNode.children = [array[index]];
      this.treeState = {
        ...this.treeState,
        expandedNodeIds: { ...this.treeState?.expandedNodeIds, [selectedNode.entityUid]: true },
      };
      this.tree?.treeModel.setState(this.treeState);
      this.insertChild(selectedNode.children[0], array, index - 1);
    }
  }

  private expandTree(): void {
    const expandedNodeIds = this.entities?.[0]?.hasChildren
      ? { [this.entities[0].entityUid]: true }
      : {};
    this.selectedPath?.forEach((value) => {
      const someNode = this.tree?.treeModel?.getNodeById(value);
      if (!someNode?.hasChildren) {
        return;
      }
      expandedNodeIds[value] = true;
      someNode?.expand();
    });

    this.treeState = {
      ...this.treeState,
      expandedNodeIds: { ...this.treeState?.expandedNodeIds, ...expandedNodeIds },
    };
  }

  private initEntities(): void {
    if (!this.selectedEntities) {
      this.selectedEntities = new Map();
    }

    const entities = cloneDeep(this.selectedEntities.get(this.searchSelector));
    this.staticListUpdateSubject$
      .pipe(debounceTime(100))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.staticFilterUpdated.emit({
          key: this.searchSelector,
          value: Array.from(this.localPreSelectedEntities.values()),
        });
      });

    if (entities) {
      const preSelectedEntities = entities?.argument?.replace('(')?.replace(')')?.split(',');
      const allEntities = preSelectedEntities?.map((entity) => {
        return {
          id: entity,
          text: this.tree?.treeModel?.getNodeById(entity)?.data?.name || '',
        };
      });
      this.listAllEntities.emit(allEntities);
      this.setSelectedEntities(entities);
    }

    if (this.allowDeleted) {
      this.updateStatusFilter();
    }
  }

  private initData(): void {
    this.initEntities();
    this.startListeningForSearch();
  }

  private updateStatusFilter(): void {
    this.filterUpdated.emit({
      key: SearchSelector.Status,
      selector: SearchSelector.Status,
      value: this.status.map((id) => ({ id })),
    });
  }

  private resetFocus(): void {
    if (this.tree?.treeModel) {
      this.tree.treeModel.getFocusedNode()?.setIsActive(false);
      this.tree.treeModel.focusedNodeId = null;

      const keys: string[] = this.tree.treeModel.activeNodeIds?.$mobx['getKeys']();
      if (keys?.length) {
        const FIRST_INDEX = 0;
        this.tree.treeModel['setActiveNode'](keys[FIRST_INDEX], null);
      }
    }
  }

  get isApplyButtonFocused(): boolean {
    this.buttonFocused.emit(document.activeElement === document.getElementById('orgApplyButton'));
    return document.activeElement === document.getElementById('orgApplyButton');
  }
  get isClearButtonFocused(): boolean {
    this.buttonFocused.emit(document.activeElement === document.getElementById('orgClearButton'));
    return document.activeElement === document.getElementById('orgClearButton');
  }
  get isLoadMoreButtonFocused(): boolean {
    this.buttonFocused.emit(
      document.activeElement === document.getElementById('orgLoadMoreButton'),
    );
    return document.activeElement === document.getElementById('orgLoadMoreButton');
  }

  private getSelectedOrgName(value: ISelectInput): string {
    return this.selectedSubOrgToEdit?.find((org) => org.entityUid === value.id)?.name ?? '';
  }

  private getEntity(
    start,
    noLoader: boolean,
    reachedTheEnd: (val: IOrganisation[]) => boolean,
  ): Observable<IOrganisation[]> {
    const params = null;
    return this.organisationService
      .getEntity(start, noLoader, this.currentRootEntity, params, this.isEntityTypeGroup)
      .pipe(tap(reachedTheEnd));
  }

  private callGetHierarchyView(node: TreeNode): Promise<IEntityHierarchy[]> {
    this.attachScrollEvent();
    return this.organisationService
      .getHierarchyView(node.id, 0, {}, String(this.limitSelectedEntityElements))
      .pipe(
        map((hierarchyList) => {
          this.setLocalExpandedHierarchyList(hierarchyList);
          return hierarchyList.filter(
            (item, index) =>
              !this.tree?.treeModel?.getNodeBy(
                (nodeItem) =>
                  nodeItem.data.entityUid === item.entityUid &&
                  hierarchyList.indexOf(item) !== index,
              ),
          );
        }),
        map((value) =>
          uniqBy(
            node.id === this.currentRootEntity
              ? [...(this.selectedNodes?.length ? this.selectedNodes : []), ...value]
              : value,
            SearchSelector.EntityUid,
          ),
        ),
      )
      .toPromise();
  }

  private callGetGroupRelationships(node: TreeNode): Promise<IEntityLightWeight[]> {
    return this.organisationService
      .getGroupRelationships(node.id)
      .pipe(
        map((hierarchyList) => {
          this.setLocalExpandedHierarchyList(hierarchyList);
          return hierarchyList.filter(
            (item, index) =>
              !this.tree?.treeModel?.getNodeBy(
                (nodeItem) =>
                  nodeItem.data.entityUid === item.entityUid &&
                  hierarchyList.indexOf(item) !== index,
              ),
          );
        }),
        map((entities: IEntityLightWeight[]) => {
          this.groupEntityRelationships = cloneDeep(uniqBy(entities, SearchSelector.EntityUid));
          this.groupEntityRelationships?.forEach((entity) => {
            if (entity?.entityUid) {
              const childNode = {
                id: entity?.entityUid,
                data: entity,
              };
              this.setDescendants(childNode as TreeNode);
            }
          });
          return uniqBy(
            node.id === this.currentRootEntity
              ? [...(this.selectedNodes?.length ? this.selectedNodes : []), ...entities]
              : entities,
            SearchSelector.EntityUid,
          );
        }),
      )
      .toPromise();
  }

  private setDescendants(node: TreeNode): void {
    this.organisationService.getTotalCount({ parentId: node.id }).subscribe((entityCount) => {
      if (entityCount?.count) {
        const entitiesTree = this.entities;
        entitiesTree[0]?.children?.forEach((entity) => {
          if (node?.id === entity.entityUid) {
            entity.count = entityCount?.count;
            entity.hasChildren = true;
          }

          if (!this.treeState?.selectedLeafNodeIds) {
            this.treeState = {
              ...this.treeState,
              selectedLeafNodeIds: {
                ...this.treeState?.selectedLeafNodeIds,
                [entity.entityUid]: false,
              },
            };
          } else if (!this.treeState?.selectedLeafNodeIds[node.id]) {
            this.treeState.selectedLeafNodeIds = {
              ...this.treeState.selectedLeafNodeIds,
              [entity.entityUid]: false,
            };
          }
          this.tree?.treeModel?.setState(this.treeState);
          this.tree?.treeModel?.update();
          this.tree?.sizeChanged();
        });
        this.entities = entitiesTree;
      }
    });
  }

  private setAddButtonLabel(): void {
    const count = this.getFilteredNodeCount();
    this.addButtonLabel = count ? $localize`Add(${count})` : $localize`Add`;
  }

  private getSeletedTreeList(): string[] {
    return Object.keys(this.treeState?.selectedLeafNodeIds || {}).filter(
      (id) => this.treeState.selectedLeafNodeIds[id],
    );
  }

  private getFilteredNodeCount(): number {
    const selectedTreeList = this.getSeletedTreeList();
    const parentNodeEntityUid = this.currentRootEntity;
    return parentNodeEntityUid &&
      selectedTreeList.includes(parentNodeEntityUid) &&
      selectedTreeList.length > 1
      ? selectedTreeList.length - 1
      : selectedTreeList.length;
  }

  private attachScrollEvent(): void {
    const onScroll = (event: any): void => {
      const element = event.target;
      // Check if browser is Edge
      const isEdge = /Edg/.test(navigator.userAgent);
      // More lenient bottom detection for Edge browser to handle scroll calculation differences
      const atBottom = isEdge
        ? element.scrollHeight - element.scrollTop <= element.clientHeight + 1
        : Math.floor(element.scrollHeight - element.scrollTop) <= element.clientHeight;
      if (atBottom && !this.loadingMore$.value && !this.isLastBatch) {
        const parentNode = this.tree?.treeModel?.getNodeById(this.currentRootEntity);
        this.loadMore(parentNode);
      }
    };

    setTimeout(() => {
      document.querySelector('tree-viewport')?.addEventListener('scroll', onScroll);
    }, 0);
  }

  private synchronizeLocalPreSelectedEntities(): void {
    if (this.selectedEntities?.get(SearchSelector.AncestorId)?.argument) {
      const expanded = { ...this.treeState.expandedNodeIds };
      const updated = { ...this.treeState.selectedLeafNodeIds };

      Array.from(this.localPreSelectedEntities.keys()).forEach((id) => {
        const nodeFromSelectedList = this.selectedEntityList?.get(id);
        const nodeFromLocalPre = this.localPreSelectedEntities.get(id);

        const node = nodeFromSelectedList ?? nodeFromLocalPre;

        const childrenFromValue = node?.value?.data?.children;
        const childrenFromParent = node?.value?.parent?.data?.children;
        const children = Array.isArray(childrenFromValue)
          ? childrenFromValue
          : Array.isArray(childrenFromParent)
          ? childrenFromParent
          : undefined;

        const hasSingleChild = !!children && children.length === 1;
        const isExpanded = !!expanded[id];

        if (isExpanded || hasSingleChild) {
          updated[id] = true;
        }
      });

      Array.from(this.intermediatePreSelectedEntities.keys())
        .filter((id) => !this.localPreSelectedEntities.has(id))
        .forEach((id) => (updated[id] = false));

      this.treeState.selectedLeafNodeIds = updated;

      const filteredselectedEntityList = new Map(
        Array.from(this.intermediateselectedEntityList.entries()).filter(([key]) =>
          this.localPreSelectedEntities.has(key),
        ),
      );

      this.selectedEntityList = filteredselectedEntityList;
    } else {
      this.treeState = undefined;
      this.selectedEntityList.clear();
    }

    this.updateEntities();
    this.setAddButtonLabel();
  }

  private resetArrowControls(): void {
    this.entityFlex = 0;
    this.treeFlex = this.WrapperHeight - this.arrowControlsHeight;
  }
}
